import { useState } from 'react';
import { Book, Calendar, User, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Story } from '@shared/schema';
import { cn } from '@/lib/utils';

interface AnimatedBookshelfProps {
  stories: Story[];
  onBookSelect: (story: Story) => void;
}

// Color themes for book spines based on story style - muted for accessibility
const bookThemes = {
  funny: {
    spine: 'bg-gradient-to-br from-amber-200 to-amber-300',
    accent: 'bg-amber-600',
    text: 'text-black'
  },
  adventurous: {
    spine: 'bg-gradient-to-br from-teal-200 to-teal-300',
    accent: 'bg-teal-600',
    text: 'text-black'
  },
  gentle: {
    spine: 'bg-gradient-to-br from-purple-200 to-purple-300',
    accent: 'bg-purple-600',
    text: 'text-black'
  },
  magical: {
    spine: 'bg-gradient-to-br from-purple-300 to-teal-200',
    accent: 'bg-purple-600',
    text: 'text-black'
  },
  educational: {
    spine: 'bg-gradient-to-br from-teal-200 to-amber-200',
    accent: 'bg-teal-600',
    text: 'text-black'
  }
};

export function AnimatedBookshelf({ stories, onBookSelect }: AnimatedBookshelfProps) {
  const [selectedBookIndex, setSelectedBookIndex] = useState<number | null>(null);
  const [currentShelf, setCurrentShelf] = useState(0);
  const booksPerShelf = 6;
  const totalShelves = Math.ceil(stories.length / booksPerShelf);

  const handleBookClick = (index: number, story: Story) => {
    setSelectedBookIndex(index);
    
    // Trigger book selection after animation
    setTimeout(() => {
      onBookSelect(story);
    }, 800);
  };

  const nextShelf = () => {
    setCurrentShelf((prev) => (prev + 1) % totalShelves);
    setSelectedBookIndex(null);
  };

  const prevShelf = () => {
    setCurrentShelf((prev) => (prev - 1 + totalShelves) % totalShelves);
    setSelectedBookIndex(null);
  };

  const currentBooks = stories.slice(
    currentShelf * booksPerShelf,
    (currentShelf + 1) * booksPerShelf
  );

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      {/* 3D Bookshelf Container */}
      <div className="relative perspective-1000">
        {/* Shelf Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-200 to-gray-300 rounded-lg shadow-lg transform-gpu" 
             style={{ transform: 'rotateX(-5deg) translateZ(-50px)' }}>
          {/* Subtle texture effect */}
          <div className="absolute inset-0 opacity-10">
            <div 
              className="h-full w-full"
              style={{ 
                backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.05) 2px, rgba(0,0,0,0.05) 4px)'
              }}
            ></div>
          </div>
        </div>

        {/* Books Container */}
        <div className="relative p-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {currentBooks.map((story, index) => {
            const globalIndex = currentShelf * booksPerShelf + index;
            const isSelected = selectedBookIndex === globalIndex;
            const theme = bookThemes[story.style as keyof typeof bookThemes] || bookThemes.magical;
            
            return (
              <div
                key={story.id}
                className="relative h-64 w-full cursor-pointer transform-gpu transition-all duration-700 hover:scale-105 hover:-translate-y-2"
                style={{ 
                  perspective: '1000px',
                  zIndex: isSelected ? 50 : 1
                }}
                onClick={() => handleBookClick(globalIndex, story)}
              >
                {/* Book Container with 3D Flip */}
                <div
                  className={cn(
                    "relative h-full w-full transform-style-3d transition-transform duration-700",
                    isSelected && "rotate-y-180"
                  )}
                  style={{ 
                    transformStyle: 'preserve-3d',
                    transform: isSelected ? 'rotateY(-180deg)' : 'rotateY(0deg)'
                  }}
                >
                  {/* Book Spine (Front) */}
                  <div
                    className={cn(
                      "absolute inset-0 backface-hidden rounded-lg shadow-lg transform-gpu",
                      theme.spine
                    )}
                    style={{ 
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(0deg)'
                    }}
                  >
                    {/* Book Details */}
                    <div className="h-full flex flex-col justify-between p-3 relative z-10">
                      {/* Title Area */}
                      <div className="flex-1 flex items-center">
                        <div className="w-full text-center space-y-1">
                          <Book className="w-6 h-6 mx-auto mb-2 text-black" />
                          <div className="text-xs font-bold leading-tight line-clamp-3 text-black">
                            {story.title || `Story for ${story.gender === 'other' ? 'Child' : story.gender}`}
                          </div>
                          <div className="text-xs font-semibold text-black">Age {story.age}</div>
                        </div>
                      </div>
                      
                      {/* Bottom Details */}
                      <div className="space-y-1">
                        <div className={cn("h-1 rounded-full opacity-70", theme.accent)} />
                        <div className="text-xs text-center capitalize font-bold text-black">
                          {story.style}
                        </div>
                      </div>
                    </div>

                    {/* Book Edge Shadow for depth */}
                    <div className="absolute inset-y-0 right-0 w-2 bg-black/10 rounded-r-lg" />
                    
                    {/* Book Pages Effect - moved to z-index 0 to not cover text */}
                    <div className="absolute inset-y-2 right-1 left-2 bg-gradient-to-r from-gray-100 to-gray-200 rounded-sm z-0" />
                    <div className="absolute inset-y-3 right-1.5 left-2.5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-sm z-0" />
                  </div>

                  {/* Book Back (Shows when flipped) */}
                  <div
                    className="absolute inset-0 backface-hidden bg-gradient-to-br from-gray-100 to-white rounded-lg shadow-lg p-4 transform-gpu"
                    style={{ 
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)'
                    }}
                  >
                    <div className="h-full flex flex-col justify-center items-center text-center space-y-3">
                      <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-8 h-8 object-contain" />
                      <p className="text-sm font-medium text-gray-700">Opening story...</p>
                      <div className="w-16 h-1 bg-purple-400 rounded-full animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Controls */}
        {totalShelves > 1 && (
          <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between pointer-events-none">
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevShelf();
              }}
              className="pointer-events-auto ml-2 p-2 bg-gray-100 hover:bg-white rounded-full shadow-md transition-all transform hover:scale-110"
              aria-label="Previous shelf"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextShelf();
              }}
              className="pointer-events-auto mr-2 p-2 bg-gray-100 hover:bg-white rounded-full shadow-md transition-all transform hover:scale-110"
              aria-label="Next shelf"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        )}

        {/* Shelf Indicator */}
        {totalShelves > 1 && (
          <div className="flex justify-center mt-6 gap-2">
            {Array.from({ length: totalShelves }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentShelf(index);
                  setSelectedBookIndex(null);
                }}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  currentShelf === index 
                    ? "bg-purple-600 w-6" 
                    : "bg-gray-300 hover:bg-gray-400"
                )}
                aria-label={`Go to shelf ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bookshelf Shadow */}
      <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-4/5 h-8 bg-black/10 rounded-full blur-xl" />
    </div>
  );
}